import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import Avatar from '@material-ui/core/Avatar'
import Button from '@material-ui/core/Button'
import IconButton from '@material-ui/core/IconButton'
import InputAdornment from '@material-ui/core/InputAdornment'
import Paper from '@material-ui/core/Paper'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import SearchIcon from '@material-ui/icons/Search'
import WhatsAppIcon from '@material-ui/icons/WhatsApp'
import {
  ListTodo,
  Pencil,
  Settings,
  SquareUserRound,
  Trash2
} from 'lucide-react'
import { useContext, useEffect, useReducer, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import { ButtonAddNew } from '../../components/ButtonAddNew'
import { ButtonSecondary } from '../../components/ButtonSecondary'
import { Can } from '../../components/Can'
import ConfirmationModal from '../../components/ConfirmationModal/'
import ContactModal from '../../components/ContactModal'
import { InputFieldText } from '../../components/InputFieldText'
import { InputSwitch } from '../../components/InputSwitch'
import MainContainer from '../../components/MainContainer'
import MainHeader from '../../components/MainHeader'
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper'
import NewTicketModalWecloud from '../../components/NewTicketModalWecloud'
import QuickTable from '../../components/QuickTable'
import { TablePaginationFooter } from '../../components/TablePaginationFooter'
import TaskModal from '../../components/TaskModal'
import { AuthContext } from '../../context/Auth/AuthContext'
import { SocketContext } from '../../context/Socket/SocketContext'
import toastError from '../../errors/toastError'
import { generateColor } from '../../helpers/colorGenerator'
import { getInitials } from '../../helpers/getInitials'
import useHelps from '../../hooks/useHelps'
import api from '../../services/api'
import { i18n } from '../../translate/i18n'
import { useStyles } from './styles'
import { InputSearch } from '../../components/InputSearch'

const reducer = (state, action) => {
  if (action.type === 'LOAD_CONTACTS') {
    const contacts = action.payload
    const newContacts = []

    contacts.forEach(contact => {
      const contactIndex = state.findIndex(c => c.id === contact.id)
      if (contactIndex !== -1) {
        state[contactIndex] = contact
      } else {
        newContacts.push(contact)
      }
    })

    return [...state, ...newContacts]
  }

  if (action.type === 'LOAD_MORE_CONTACTS') {
    const contacts = action.payload
    // Para "load more", simplesmente adiciona os novos contatos ao final da lista
    return [...state, ...contacts]
  }

  if (action.type === 'UPDATE_CONTACTS') {
    const contact = action.payload
    const contactIndex = state.findIndex(c => c.id === contact.id)

    if (contactIndex !== -1) {
      state[contactIndex] = contact
      return [...state]
    } else {
      return [contact, ...state]
    }
  }

  if (action.type === 'DELETE_CONTACT') {
    const contactId = action.payload

    const contactIndex = state.findIndex(c => c.id === contactId)
    if (contactIndex !== -1) {
      state.splice(contactIndex, 1)
    }
    return [...state]
  }

  if (action.type === 'RESET') {
    return []
  }
}

const Contacts = () => {
  const classes = useStyles()
  const history = useHistory()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const { user } = useContext(AuthContext)

  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [searchParam, setSearchParam] = useState('')
  const [contacts, dispatch] = useReducer(reducer, [])
  const [selectedContactId, setSelectedContactId] = useState(null)
  const [contactModalOpen, setContactModalOpen] = useState(false)
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false)
  const [contactTicket, setContactTicket] = useState({})
  const [deletingContact, setDeletingContact] = useState(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [totalContacts, setTotalContacts] = useState(0)

  const [filterSilent, setFilterSilent] = useState('')
  const [filterBlacklist, setFilterBlacklist] = useState('')
  const [filterDisableBot, setFilterDisableBot] = useState('')
  const [helps, setHelps] = useState([])
  const [vCardContactData, setVCardContactData] = useState(null)
  const [taskModalOpen, setTaskModalOpen] = useState(false)
  const [selectedTaskContactId, setSelectedTaskContactId] = useState(null)

  const [rowsPerPage, setRowsPerPage] = useState(25)

  const { list: listHelps } = useHelps()

  const socketManager = useContext(SocketContext)

  useEffect(() => {
    dispatch({ type: 'RESET' })
    setPageNumber(1)
  }, [
    searchParam,
    filterBlacklist,
    filterSilent,
    filterDisableBot,
    rowsPerPage
  ])

  useEffect(() => {
    setLoading(true)

    const delayDebounceFn = setTimeout(() => {
      let params = { searchParam, pageNumber, limit: rowsPerPage }

      if (filterSilent) {
        params = { ...params, silencioso: filterSilent }
      }

      if (filterBlacklist) {
        params = { ...params, blacklist: filterBlacklist }
      }

      if (filterDisableBot) {
        params = { ...params, disableBot: filterDisableBot }
      }

      const fetchContacts = async () => {
        try {
          const { data } = await api.get('/contacts/', {
            params
          })

          dispatch({ type: 'RESET' })
          dispatch({ type: 'LOAD_CONTACTS', payload: data.contacts })

          setHasMore(data.hasMore)
          setTotalContacts(data.count || 0)
        } catch (err) {
          toastError(err)
        } finally {
          setLoading(false)
        }
      }
      fetchContacts()
    }, 500)
    return () => clearTimeout(delayDebounceFn)
  }, [
    searchParam,
    filterBlacklist,
    filterSilent,
    filterDisableBot,
    pageNumber,
    rowsPerPage
  ])

  useEffect(() => {
    const fetchHelps = async () => {
      try {
        const helpsData = await listHelps()
        setHelps(helpsData)
        console.log('Helps data:', helpsData)
      } catch (err) {
        toastError(err)
      }
    }

    fetchHelps()
  }, []) // Array vazio para executar apenas uma vez ao montar o componente

  useEffect(() => {
    const companyId = localStorage.getItem('companyId')
    const socket = socketManager.GetSocket(companyId)

    const onContact = data => {
      if (data.action === 'update' || data.action === 'create') {
        dispatch({ type: 'UPDATE_CONTACTS', payload: data.contact })
      }

      if (data.action === 'delete') {
        dispatch({ type: 'DELETE_CONTACT', payload: +data.contactId })
      }
    }

    socket.on(`company-${companyId}-contact`, onContact)

    return () => {
      socket.disconnect()
    }
  }, [socketManager])

  // Listener para evento de adicionar contato do vCard
  useEffect(() => {
    const handleAddContactFromVCard = event => {
      setVCardContactData(event.detail)
      setContactModalOpen(true)
    }

    window.addEventListener('addContactFromVCard', handleAddContactFromVCard)

    return () => {
      window.removeEventListener(
        'addContactFromVCard',
        handleAddContactFromVCard
      )
    }
  }, [])

  const handleSearch = event => {
    setSearchParam(event.target.value.toLowerCase())
  }

  const handleOpenContactModal = () => {
    setSelectedContactId(null)
    setContactModalOpen(true)
  }

  const handleCloseContactModal = () => {
    setSelectedContactId(null)
    setContactModalOpen(false)
    setVCardContactData(null) // Limpar dados do vCard
  }

  const handleCloseOrOpenTicket = ticket => {
    setNewTicketModalOpen(false)
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`)
    }
  }

  const handleEditContact = contactId => {
    setSelectedContactId(contactId)
    setContactModalOpen(true)
  }

  const handleDeleteContact = async contactId => {
    try {
      await api.delete(`/contacts/${contactId}`)
      toast.success(i18n.t('contacts.toasts.deleted'))
    } catch (err) {
      toastError(err)
    }
    setDeletingContact(null)
    // Removido o reset desnecessário do searchParam e pageNumber
    // O socket já atualiza a lista automaticamente quando um contato é deletado
  }

  const handleOpenTaskModal = contactId => {
    setSelectedTaskContactId(contactId)
    setTaskModalOpen(true)
  }

  const handleCloseTaskModal = () => {
    setSelectedTaskContactId(null)
    setTaskModalOpen(false)
  }

  const handleimportContact = async () => {
    try {
      await api.post('/contacts/import')
      history.go(0)
    } catch (err) {
      toastError(err)
    }
  }

  const handleChangePage = (event, newPage) => {
    setPageNumber(newPage)
  }

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPageNumber(1)
  }

  const columns = [
    {
      field: 'profilePic',
      headerName: '',
      align: 'left',
      style: { paddingRight: 0 }
    },
    {
      field: 'name',
      headerName: i18n.t('contacts.table.name'),
      align: 'left'
    },
    {
      field: 'number',
      headerName: i18n.t('contacts.table.whatsapp'),
      align: 'left'
    },
    {
      field: 'blacklist',
      headerName: 'Contato',
      align: 'left'
    },
    {
      field: 'silencioso',
      headerName: 'Silencioso',
      align: 'left'
    },
    {
      field: 'email',
      headerName: i18n.t('contacts.table.email'),
      align: 'left'
    }
  ]

  // Renderizador personalizado para células do QuickTable
  const customCellRenderer = {
    profilePic: contact => (
      <Avatar
        style={{
          backgroundColor: generateColor(contact?.number),
          fontWeight: 'bold',
          fontSize: '.875rem',
          color: 'white',
          height: '25px',
          width: '25px'
        }}
        src={contact.profilePicUrl}
      >
        {getInitials(contact?.name)}
      </Avatar>
    ),
    blacklist: contact => (
      <span>{contact.blacklist ? 'Bloqueado' : 'Desbloqueado'}</span>
    ),
    silencioso: contact => <span>{contact.silencioso ? 'Sim' : 'Não'}</span>
  }

  const customActionsRenderer = contact => {
    return (
      <>
        <IconButton
          size="small"
          onClick={() => {
            setContactTicket(contact)
            setNewTicketModalOpen(true)
          }}
          className={classes.buttonActionWhatsapp}
        >
          <WhatsAppIcon />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => handleOpenTaskModal(contact.id)}
          className={classes.buttonActionEdit}
          title="Nova Tarefa"
        >
          <ListTodo size={20} />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => handleEditContact(contact.id)}
          className={classes.buttonActionEdit}
        >
          <Settings size={20} />
        </IconButton>
        <Can
          role={user.profile}
          perform="contacts-page:deleteContact"
          yes={() => (
            <IconButton
              size="small"
              onClick={() => {
                setConfirmOpen(true)
                setDeletingContact(contact)
              }}
              className={classes.buttonActionDelete}
            >
              <Trash2 size={20} />
            </IconButton>
          )}
        />
      </>
    )
  }

  const handleSilentSwitchChange = event => {
    const value = event.target.checked
    setFilterSilent(value ? 'true' : '')
    console.log('Silencioso:', filterSilent)
  }

  const handleBlacklistSwitchChange = event => {
    const value = event.target.checked
    setFilterBlacklist(value ? 'true' : '')
    console.log('Blacklist:', filterBlacklist)
  }

  const handleDisableBotSwitchChange = event => {
    const value = event.target.checked
    setFilterDisableBot(value ? 'true' : '')
    console.log('DisableBot:', filterDisableBot)
  }

  const renderHelpSection = () => {
    if (helps.length === 0) return null

    const contactsHelps = helps.filter(h => h.link === 'Contatos')
    if (contactsHelps.length === 0) return null

    return (
      <Box mb={2}>
        <Typography variant="h6" color="primary" gutterBottom>
          📋 Ajuda - Contatos
        </Typography>
        {contactsHelps.map((help, index) => (
          <Accordion key={index} style={{ marginBottom: '8px' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">{help.title}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box>
                {help.description && (
                  <Typography variant="body2" paragraph>
                    {help.description}
                  </Typography>
                )}
                <Box display="flex" gap={2} mt={1}>
                  {help.video && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() =>
                        window.open(
                          `https://www.youtube.com/watch?v=${help.video}`,
                          '_blank'
                        )
                      }
                      startIcon={<span>📹</span>}
                    >
                      Ver Tutorial
                    </Button>
                  )}
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    )
  }

  return (
    <MainContainer className={classes.mainContainer}>
      <NewTicketModalWecloud
        modalOpen={newTicketModalOpen}
        initialContact={contactTicket}
        onClose={ticket => {
          handleCloseOrOpenTicket(ticket)
        }}
      />
      <ContactModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        contactId={selectedContactId}
        initialValues={vCardContactData}
        onSave={() => {
          setVCardContactData(null) // Limpar dados do vCard após salvar
        }}
      ></ContactModal>
      <TaskModal
        open={taskModalOpen}
        onClose={handleCloseTaskModal}
        initialContactId={selectedTaskContactId}
      />
      <ConfirmationModal
        title={
          deletingContact
            ? `${i18n.t('contacts.confirmationModal.deleteTitle')} ${
                deletingContact.name
              }?`
            : `${i18n.t('contacts.confirmationModal.importTitlte')}`
        }
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={e =>
          deletingContact
            ? handleDeleteContact(deletingContact.id)
            : handleimportContact()
        }
      >
        {deletingContact
          ? `${i18n.t('contacts.confirmationModal.deleteMessage')}`
          : `${i18n.t('contacts.confirmationModal.importMessage')}`}
      </ConfirmationModal>
      <MainHeader
        title={i18n.t('contacts.title')}
        description="Gerencie todos os seus contatos, centralizando e organizando informações para facilitar o atendimento."
        showTutorial={!isMobile}
        // onTutorialClick={handleOpenTutorial}
      >
        <MainHeaderButtonsWrapper>
          <ButtonSecondary
            label={i18n.t('contacts.buttons.import')}
            onClick={e => setConfirmOpen(true)}
          />
          <ButtonAddNew
            onClick={handleOpenContactModal}
            startIcon={<SquareUserRound size={20} />}
            label="Novo Contato"
          />
        </MainHeaderButtonsWrapper>
      </MainHeader>

      {renderHelpSection()}

      <Grid container className={classes.boxForms}>
        <Grid item xs={12} className={classes.selectContainer}>
          <InputSearch
            placeholder="Pesquisar"
            value={searchParam}
            onChange={handleSearch}
          />
          <InputSwitch
            label="Silencioso"
            size="small"
            value={filterSilent === 'true'}
            onChange={handleSilentSwitchChange}
          />

          <InputSwitch
            label="Bloqueado"
            size="small"
            value={filterBlacklist === 'true'}
            onChange={handleBlacklistSwitchChange}
          />

          <InputSwitch
            label="Bot"
            size="small"
            value={filterDisableBot === 'true'}
            onChange={handleDisableBotSwitchChange}
          />
        </Grid>
      </Grid>
      <Paper className={classes.mainPaper} variant="outlined">
        <QuickTable
          data={contacts}
          columns={columns}
          loading={loading}
          customCellRenderer={customCellRenderer}
          customActionsRenderer={customActionsRenderer}
          actions={['custom']}
          emptyMessage="Nenhum contato encontrado."
          skeletonRows={3}
          showAlternatingRows={true}
          containerProps={{ elevation: 0 }}
        />

        <TablePaginationFooter
          pageNumber={pageNumber}
          rowsPerPage={rowsPerPage}
          totalItems={totalContacts}
          loading={loading}
          onChangePage={setPageNumber}
          onChangeRowsPerPage={setRowsPerPage}
          title="contatos"
        />
      </Paper>
    </MainContainer>
  )
}

export default Contacts
